import api from '@/services/api'
import { useConfig } from '@/composables/game/config'
import { type FortuneWheelPrize, type PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import type { PlayerStats } from '@/types/shared/player'
import { computed, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { thisOccurrence } from '@/helpers/date'
import { useNow } from '@/composables/game/time'
import { notifySpinForUtmSource, syncPlayer } from '@/composables/game/player'
import { useOnboarding } from '@/composables/game/onboarding'
import { sleep } from '@/services/helper'

export class SpinFailed extends Error {
}

export const useFortuneWheel = () => {
  const { config } = useConfig()
  const { isOnboardingDone, doOnboarding } = useOnboarding()

  const AUTO_SPIN_LIMIT = 10
  const ONBOARDING_LIMIT = 1

  const availableSpins = computed(() => fortuneWheelState.value.spins.available)
  const isAutoSpin = ref<boolean>(false)
  const canAutoSpin = computed(() => availableSpins.value > AUTO_SPIN_LIMIT)
  const isAutoSpinOnHold = ref<boolean>(false)

  const fortuneWheelState = useLocalStorage<PlayerFortuneWheelState>(buildDropeeStorageKey('fortuneWheelState'), {
    usdtCentsBalance: 0,
    usdtCentsBalanceAllTime: 0,
    spins: {
      total: 0,
      available: 0,
      lastDailyTicketTimestamp: 0,
      maxStreak: 0,
      streak: 0,
      lastSpinDate: '',
    },
    cards: {},
    lastWonPrizes: [],
    withdrawals: [],
  } satisfies PlayerFortuneWheelState)

  const lasWonPrizesCount = computed(() => fortuneWheelState.value.lastWonPrizes.length)

  async function checkOnboardingState() {
    if (lasWonPrizesCount.value >= ONBOARDING_LIMIT && !isOnboardingDone.value) {
      await doOnboarding()
    }
  }

  async function updateState(state?: PlayerFortuneWheelState) {
    const result = state ? { state } : await api.getFortuneWheelState()
    fortuneWheelState.value = result.state
  }

  async function spin(): Promise<{
    prize: FortuneWheelPrize,
    playerStats: PlayerStats,
    fortuneWheelPlayerState: PlayerFortuneWheelState
  } | null> {
    for (let i = 0; i < 5; i++) {
      try {
        const result = await api.spin()
        if (!result.prize) {
          continue
        }
        fortuneWheelState.value = result.fortuneWheelPlayerState
        if (notifySpinForUtmSource.value) {
          api.notifySpin(notifySpinForUtmSource.value).then()
          notifySpinForUtmSource.value = ''
        }
        return result
      } catch (e) {
        await sleep(500)
        await syncPlayer()
        await updateState()
        continue
      }
    }
    return null
  }

  function updateAutoSpin(value: boolean) {
    isAutoSpin.value = value
  }

  return {
    spin: spin,
    updateState: updateState,
    fortuneWheelState,
    availableSpins,
    prizes: config.value.game.fortuneWheel.prizes,
    currentBalance: computed(() => fortuneWheelState.value.usdtCentsBalance / 100),
    isAutoSpin,
    canAutoSpin,
    isAutoSpinOnHold,
    updateAutoSpin,
    AUTO_SPIN_LIMIT,
    checkOnboardingState,
    lasWonPrizesCount,
  }
}

export const useGoldWheel = () => {
  const { fortuneWheelState } = useFortuneWheel()
  const { config } = useConfig()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const isTodayClaimed = computed(() => fortuneWheelState.value.spins.lastSpinDate === todayDate.value)
  const completedStreakCount = computed(() => fortuneWheelState.value.spins.streak)
  const streak = computed<{ index: number, active: boolean, completed: boolean }[]>(() => {
    return [...Array(7).keys()].map((element, index) => {
      return {
        index: index + 1,
        active: completedStreakCount.value === index,
        completed: completedStreakCount.value > index,
      }
    })
  })

  const isGold = computed<boolean>(() => fortuneWheelState.value.spins.streak === config.value.game.fortuneWheel.goldenWheel.activation.afterDays)

  return {
    streak,
    isGold,
    isTodayClaimed,
    completedStreakCount,
  }
}
